import {
  useQuery,
  skipToken,
  type UseQueryOptions,
} from "@tanstack/react-query";

import api, { type ExtendedRequestInit } from "@/api";
import {
  ApiCatalog,
  type ApiCatalogKey,
  type HasQueryParams,
  type HasMockData,
} from "./catalogs/ApiCatalog";

interface Settings extends ExtendedRequestInit {
  skip?: boolean;
}

/**
 * @deprecated This hook is no longer maintained. Please use `useQuery` from `@tanstack/react-query` directly instead.
 */
export const useApiQuery = <Key extends ApiCatalogKey>(props: {
  apiKey: Key;
  queryParams?: HasQueryParams<Key>;
  apiSettings?: Settings;
  tanstackOptions?: Omit<
    UseQueryOptions<HasMockData<Key>>,
    "queryFn" | "queryKey"
  >;
}) => {
  const { apiKey, queryParams, apiSettings = {}, tanstackOptions = {} } = props;
  const { skip, ...restOfSettings } = apiSettings;
  const apiFilters = queryParams || {};

  const apiEndpoint = ApiCatalog[apiKey];
  let hasEndpoint = false;
  let endpoint = "";
  if ("endpoint" in apiEndpoint) {
    hasEndpoint = true;
    endpoint = apiEndpoint.endpoint;
  }
  type ResponseType = HasMockData<Key>;
  let mockData;
  if ("mockData" in apiEndpoint) {
    mockData = apiEndpoint.mockData as ResponseType;
  }
  const { isPending, data, ...rest } = useQuery({
    ...tanstackOptions,
    queryKey: [ApiCatalog[apiKey].key, queryParams],
    queryFn:
      !hasEndpoint || skip
        ? skipToken
        : async () =>
            await api().get<ResponseType>(endpoint, {
              queryParams: apiFilters,
              ...restOfSettings,
            }),
  });

  return {
    ...rest,
    isLoading: skip ? false : isPending,
    data: skip ? mockData : data,
  };
};
